import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthenticator } from '@aws-amplify/ui-react';
import * as Sentry from '@sentry/react';
import { AppDispatch } from 'src/infra/stores/store';
import { getUserActionCreator } from 'src/infra/stores/reducer/auth';
import authSelectors from 'src/infra/stores/reducer/auth/selectors';
import PageLoader from 'src/presentations/components/molecules/pageLoader';
import { selectSelectedBilledEntities, setSelectedBilledEntities } from 'src/infra/stores/reducer/billedEntitiesSlice';
import { BilledEntityItem } from 'src/use-cases/settings/useBilledEntities';
import { useUserPermissionTypes } from '../use-cases/user/useUserPermissions';
import { UserContext } from '../use-cases/user/userDetails/context';
import { useUserDetails } from '../use-cases/user/userDetails/hooks';
import { userStore } from '../use-cases/user/userDetails/userStore';
import { useGetDomainMetaData, useGetCurrencies } from '../use-cases/invoice/useInvoices';

/**
 * UserDataSynchronizer - Consolidated component for user data initialization
 *
 * This component handles:
 * 1. User authentication state
 * 2. Fetching user details from API
 * 3. Loading user permissions
 * 4. Setting up default billed entities
 * 5. Sentry user identification
 *
 * The application will show a loading indicator until all necessary data is loaded.
 */
export const UserDataSynchronizer: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const user = useSelector(authSelectors.selectUser);
  const selectedEntities = useSelector(selectSelectedBilledEntities);
  const [isInitialized, setIsInitialized] = useState(false);

  // Get the current path to determine if we're on the login page
  const currentPath = window.location.pathname;
  const isLoginPage = currentPath.includes('/login');

  // Only enable data fetching if authenticated and not on login page
  const shouldFetchData = authStatus === 'authenticated' && !isLoginPage;

  // Use all properties from the useUserDetails hook
  const userDetailsResult = useUserDetails();
  const { userDetails, isLoading: isLoadingUserDetails } = userDetailsResult;

  // Fetch permissions once at app init - uses default caching (5min stale, 10min cache)
  // Other components will use cached data, avoiding duplicate API calls
  const { isLoading: isLoadingPermissions } = useUserPermissionTypes({
    enabled: shouldFetchData,
  });

  // Prefetch common data that pages will need - this populates the cache early
  // so pages don't have to wait for these API calls when navigating
  useGetDomainMetaData();
  useGetCurrencies('DASHBOARD');

  // Set up Sentry user identification
  useEffect(() => {
    if (user?.email || user?.id) {
      Sentry.setUser({ email: user?.email, id: user?.id });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Fetch user info when authentication status changes
  useEffect(() => {
    if (authStatus === 'authenticated') {
      dispatch(getUserActionCreator());
    }
  }, [authStatus, dispatch]);

  // Initialize billed entities when user details are loaded
  useEffect(() => {
    if (shouldFetchData && !isLoadingUserDetails && userDetails && selectedEntities.length === 0) {
      if (userDetails.billedEntity) {
        const defaultEntity = userDetails.billedEntity;

        // Recursive function to map entity and all nested subEntities
        const mapEntityRecursively = (entity: any): BilledEntityItem => ({
          id: entity.id || 0,
          name: entity.name || '',
          code: entity.code || '',
          active: entity.active,
          nationalCurrency: entity.nationalCurrency,
          parentId: entity.parentId,
          isBilledEntity: entity.isBilledEntity ?? true,
          addresses: entity.addresses,
          nameAliases: entity.nameAliases,
          // Recursively map all nested subEntities
          subEntities: entity.subEntities ? entity.subEntities.map((subEntity: any) => mapEntityRecursively(subEntity)) : [],
          selected: true, // Mark as selected
        });

        // Create a billed entity item from the default entity in user profile
        const defaultBilledEntity = mapEntityRecursively(defaultEntity);

        // Dispatch action to update Redux store directly
        dispatch(setSelectedBilledEntities([defaultBilledEntity]));
      }
    }
  }, [shouldFetchData, isLoadingUserDetails, userDetails, selectedEntities.length, dispatch]);

  // Sync user details with the userStore when they change
  useEffect(() => {
    if (shouldFetchData && !isLoadingUserDetails && userDetails) {
      // Update the userStore
      userStore.setUserDetails(userDetails);

      // Mark initialization as complete when all data is loaded
      if (!isLoadingPermissions) {
        setIsInitialized(true);
      }
    }
  }, [shouldFetchData, isLoadingUserDetails, userDetails, isLoadingPermissions]);

  // Show loading indicator while any data is still loading
  if (shouldFetchData && (!isInitialized || isLoadingUserDetails || isLoadingPermissions)) {
    return <PageLoader />;
  }

  // Provide user details via context and render children
  return <UserContext.Provider value={userDetailsResult}>{children}</UserContext.Provider>;
};
